import { motion } from "framer-motion";
import { HERO } from "@/constants/testIds";
import CountUp from "@/components/site/CountUp";

const HERO_BG =
  "https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?q=90&w=2800&auto=format&fit=crop";

// Masked line reveal — each line clipped, rises into view on load
const REVEAL = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Line = ({ i, children, className = "" }) => (
  // pb + negative mb creates a taller clip window so descenders (g, y, p)
  // aren't cut off, without adding extra visual gap between lines.
  <span className="block overflow-hidden pb-[0.18em] -mb-[0.18em]">
    <motion.span
      variants={REVEAL}
      custom={i}
      initial="hidden"
      animate="show"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end"
    >
      {/* Static background */}
      <div className="absolute inset-0">
        {/* Full-quality architectural image — crisp, no blanket dimming */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        {/* Localized left-side gradient — legibility behind the headline only */}
        <div className="absolute inset-y-0 left-0 w-[65%] bg-gradient-to-r from-[#F7F6F3] via-[#F7F6F3]/70 to-transparent" />
        {/* Bottom fade into the page */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#F7F6F3]" />
        {/* Very subtle Royal Green ambient */}
        <div
          aria-hidden
          className="absolute left-[-10%] top-[20%] w-[60vw] h-[60vw] max-w-[900px] max-h-[900px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(19,98,7,0.08) 0%, rgba(19,98,7,0) 60%)",
            filter: "blur(40px)",
          }}
        />
      </div>

      {/* Top-right meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.8 }}
        className="absolute top-28 right-6 lg:right-10 z-10 hidden md:flex flex-col items-end gap-2"
      >
        <span className="overline">Global Investment Footprint</span>
        <span className="overline text-[#8A8880]">Est. Adam Holdings</span>
      </motion.div>

      {/* Side label */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 z-10 hidden lg:block"
      >
        <span className="overline rotate-180 [writing-mode:vertical-rl]">
          Private Investment · Strategic Capital
        </span>
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pb-16 pt-36">
        <div className="max-w-5xl">
          {/* Overline tag */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="inline-block w-10 h-px bg-[#136207]" />
            <span className="overline text-[#136207]">
              Meezan Ventures · Private Investment Platform
            </span>
          </motion.div>

          <h1 className="font-display text-[#1A1A1A] text-[40px] sm:text-[60px] lg:text-[88px] leading-[1.08] tracking-tight font-medium">
            <Line i={0}>
              Building <span className="italic font-light text-[#1A1A1A]">enterprises.</span>
            </Line>
            <Line i={1} className="mt-3 sm:mt-6 lg:mt-8">
              Backing <span className="italic font-light text-[#1A1A1A]">founders.</span>
            </Line>
            <Line i={2} className="mt-3 sm:mt-6 lg:mt-8 text-[#136207]">
              Compounding capital across cycles.
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-2xl text-[#5C5A55] text-base sm:text-lg leading-relaxed font-light"
          >
            Meezan Ventures is a private investment platform deploying
            strategic capital across technology, energy, resources,
            infrastructure and agritech with conviction, discipline and a long
            horizon.
          </motion.p>
        </div>

        {/* Stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#E0DDD6] border border-[#E0DDD6] backdrop-blur-sm max-w-4xl"
        >
          <StatCell
            label="Portfolio Companies"
            value={
              <>
                <CountUp to={35} duration={1.6} className="font-display" />
                <span className="text-[#136207]">+</span>
              </>
            }
          />
          <StatCell
            label="Strategic Sectors"
            value={<CountUp to={7} duration={1.4} className="font-display" />}
          />
          <StatCell
            label="Geographies"
            value={
              <>
                <CountUp to={20} duration={1.6} className="font-display" />
                <span className="text-[#136207]">+</span>
              </>
            }
          />
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="overline text-[#8A8880]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px h-8 bg-gradient-to-b from-[#136207] to-transparent"
        />
      </motion.div>
    </section>
  );
};

const StatCell = ({ label, value }) => (
  <div className="bg-[#FFFFFF]/70 px-6 py-8 backdrop-blur text-center">
    <div className="font-display text-[#1A1A1A] text-6xl md:text-7xl lg:text-[80px] tracking-tight leading-none">
      {value}
    </div>
    <div className="overline mt-6">{label}</div>
  </div>
);

export default Hero;
